<template>
  <v-menu v-model="open" location="bottom end" :close-on-content-click="false">
    <template #activator="{ props }">
      <!-- The badge wraps the button: inside it, the button would clip the count -->
      <v-badge :model-value="unread.length > 0" :content="badgeContent" color="error" offset-x="6" offset-y="6">
        <v-btn
          v-bind="props"
          icon
          variant="text"
          :title="unread.length ? `${unread.length} notification(s) non lue(s)` : 'Notifications'"
        >
          <v-icon>{{ unread.length ? "mdi-bell-ring" : "mdi-bell-outline" }}</v-icon>
        </v-btn>
      </v-badge>
    </template>
    <v-card min-width="320" max-width="420">
      <v-card-title class="d-flex align-center">
        Notifications
        <v-spacer></v-spacer>
        <v-btn v-if="unread.length" size="small" variant="text" color="primary" @click="markAllRead">
          Tout marquer comme lu
        </v-btn>
      </v-card-title>
      <v-list v-if="unread.length" lines="three" density="compact" class="py-0 notification-list">
        <v-list-item v-for="notification in unread" :key="notification.id" @click="openNotification(notification)">
          <template #prepend>
            <v-icon :color="notification.type === 'danger_flagged' ? 'warning' : 'error'">
              {{ notification.type === "danger_flagged" ? "mdi-flag" : "mdi-alert" }}
            </v-icon>
          </template>
          <v-list-item-title class="text-wrap font-weight-medium">{{ title(notification) }}</v-list-item-title>
          <v-list-item-subtitle>{{ details(notification) }}</v-list-item-subtitle>
          <template #append>
            <v-btn
              icon="mdi-check"
              size="x-small"
              variant="text"
              title="Marquer comme lu"
              @click.stop="markRead([notification.id])"
            ></v-btn>
          </template>
        </v-list-item>
      </v-list>
      <v-card-text v-else class="text-medium-emphasis">Aucune nouvelle notification.</v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          variant="text"
          prepend-icon="mdi-flag-outline"
          :to="{ name: 'admin-danger-flags' }"
          @click="open = false"
        >
          Dangers signalés
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
  import { AdminNotificationsService } from "@/store/modules/adminNotifications";
  import { notifyError } from "@/services/Notification.service";

  const relativeTime = new Intl.RelativeTimeFormat("fr-CA", { numeric: "auto" });

  // Unread admin notifications (new dangers, dangers flagged to the admins), live. Shown to admins only.
  export default {
    name: "AdminNotificationsBell",
    data() {
      return { open: false, unread: [], unsubscribe: null };
    },
    computed: {
      badgeContent() {
        return this.unread.length > 99 ? "99+" : this.unread.length;
      },
    },
    mounted() {
      this.unsubscribe = AdminNotificationsService.actions.watchUnread(
        (notifications) => (this.unread = notifications),
        (error) => console.error("Error watching admin notifications:", error),
      );
    },
    beforeUnmount() {
      if (this.unsubscribe) this.unsubscribe();
    },
    methods: {
      title(notification) {
        const section = [notification.river, notification.section_name].filter(Boolean).join(" - ");
        return notification.type === "danger_flagged"
          ? `Danger signalé aux administrateurs : ${section}`
          : `Nouveau danger : ${section}`;
      },
      details(notification) {
        const parts = [];
        if (notification.rapid_name) parts.push(notification.rapid_name);
        if (notification.by && notification.by.displayName) parts.push(`par ${notification.by.displayName}`);
        if (notification.comment) parts.push(`« ${notification.comment} »`);
        parts.push(this.timeAgo(notification.created_at));
        return parts.filter(Boolean).join(" · ");
      },
      timeAgo(timestamp) {
        if (!timestamp || typeof timestamp.toMillis !== "function") return "";
        const minutes = Math.round((timestamp.toMillis() - Date.now()) / 60000);
        if (Math.abs(minutes) < 60) return relativeTime.format(minutes, "minute");
        const hours = Math.round(minutes / 60);
        if (Math.abs(hours) < 24) return relativeTime.format(hours, "hour");
        return relativeTime.format(Math.round(hours / 24), "day");
      },
      async markRead(ids) {
        try {
          await AdminNotificationsService.actions.markRead(ids);
        } catch (error) {
          console.error("Error marking notifications read:", error);
          notifyError("Les notifications n'ont pas pu être marquées comme lues.");
        }
      },
      markAllRead() {
        return this.markRead(this.unread.map((notification) => notification.id));
      },
      async openNotification(notification) {
        this.open = false;
        await this.markRead([notification.id]);
        if (notification.type === "danger_flagged") {
          this.$router.push({ name: "admin-danger-flags" });
        } else if (notification.section_alias) {
          this.$router.push({
            name: "riviere",
            params: { slug: notification.section_alias },
            query: { onglet: "dangers" },
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .notification-list {
    max-height: 420px;
    overflow-y: auto;
  }
</style>
